import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { Badge, Pane, ProjectsIcon, SideSheet } from "evergreen-ui";
import { MultiTabView } from "../../common/multitab/MultiTabView";
import { TabInfo } from "../../common/multitab/TabInfo";
import { CostMetadata, CostPayoutModifier, ProductListingToPayoutRule } from "../../../../common/models";
import { CostMetadataTableRow } from "../../../../common/tables/cost-metadata/CostMetadataTableRow";
import { PLPRTableRow } from "../../../../common/tables/product-listing/payout-rule-mapping/PLPRTableRow";
import { QueryInput } from "../../../../common/query-filters";
import { AbstractTableViewProvider } from "../../common/tables/view/AbstractTableViewProvider";
import { TableView } from "../../common/tables/view/TableView";
import { CostMetadataTableCellFactory } from "../MultiTabProductPage/cost-metadata/factory/CostMetadataTableCellFactory";
import { PLPRTableCellFactory } from "../MultiTabProductPage/payout-rule-mapping/factory/PLPRTableCellFactory";
import { DefaultCostMetadataSideSheetTableConfig } from "./tables/config/DefaultCostMetadataSideSheetTableConfig";
import { DefaultPLPRSideSheetTableConfig } from "./tables/config/DefaultPLPRSideSheetTableConfig";
import { queryCostPayoutModifierById } from "../../datastore/CostPayoutModifierDataStore";
import { TableCellFactory } from "../../common/tables/factory";

export type CostPayoutModifierWithRelations = CostPayoutModifier & {
    costMetadata?: CostMetadata[];
    payoutRuleMappings?: ProductListingToPayoutRule[];
};

const TAB_LABELS = ["Cost Metadata", "Payout Rule Mappings"];

interface CPMDetailSideSheetProps {
    isShown: boolean;
    costPayoutModifier: CostPayoutModifier | null;
    initialTabIndex: number;
    onDismiss: () => void;
}

export const CPMDetailSideSheet: FC<CPMDetailSideSheetProps> = ({
    isShown,
    costPayoutModifier,
    initialTabIndex,
    onDismiss,
}) => {
    const [selectedTabIndex, setSelectedTabIndex] = useState<number>(initialTabIndex);
    const [cpmDetail, setCpmDetail] = useState<CostPayoutModifierWithRelations | null>(null);

    useEffect(() => {
        setSelectedTabIndex(initialTabIndex);
    }, [initialTabIndex]);

    const cpmId = costPayoutModifier?.id;

    useEffect(() => {
        if (isShown && cpmId) {
            queryCostPayoutModifierById(cpmId)
                .then((result) => {
                    setCpmDetail(result as CostPayoutModifierWithRelations);
                })
                .catch(() => {
                    setCpmDetail(null);
                });
        }

        if (!isShown) {
            setCpmDetail(null);
        }
    }, [isShown, cpmId]);

    const tabs: TabInfo[] = useMemo(() => {
        if (!cpmDetail || !costPayoutModifier) return [];
        return [
            {
                label: TAB_LABELS[0],
                tabId: "cost-metadata",
                component: (
                    <TabContentCostMetadata
                        costMetadata={cpmDetail.costMetadata ?? []}
                        cpmName={costPayoutModifier.name}
                    />
                ),
            },
            {
                label: TAB_LABELS[1],
                tabId: "payout-rule-mappings",
                component: (
                    <TabContentPLPR
                        payoutRuleMappings={cpmDetail.payoutRuleMappings ?? []}
                        cpmName={costPayoutModifier.name}
                    />
                ),
            },
        ];
    }, [cpmDetail, costPayoutModifier]);

    if (!costPayoutModifier) {
        return null;
    }

    return (
        <SideSheet
            width={"75vw"}
            isShown={isShown}
            onCloseComplete={onDismiss}
        >
            {tabs.length > 0 && (
                <MultiTabView
                    embedded
                    header={<CPMHeaderSummary costPayoutModifier={costPayoutModifier} />}
                    tabs={tabs}
                    selectedTabIndex={selectedTabIndex}
                    onTabSelected={setSelectedTabIndex}
                />
            )}
        </SideSheet>
    );
};

const CPMHeaderSummary: FC<{ costPayoutModifier: CostPayoutModifier }> = ({ costPayoutModifier }) => {
    const conditions: { label: string; value: string }[] = [];

    if (costPayoutModifier.conditionCountryCode) {
        conditions.push({ label: "Country", value: costPayoutModifier.conditionCountryCode });
    }
    if (costPayoutModifier.conditionEmailDomains && costPayoutModifier.conditionEmailDomains.length > 0) {
        conditions.push({ label: "Email", value: costPayoutModifier.conditionEmailDomains.join(", ") });
    }
    if (costPayoutModifier.conditionIsBundle !== undefined && costPayoutModifier.conditionIsBundle !== null) {
        conditions.push({ label: "Bundle", value: costPayoutModifier.conditionIsBundle ? "Yes" : "No" });
    }
    if (costPayoutModifier.conditionSourceName) {
        conditions.push({ label: "Source", value: costPayoutModifier.conditionSourceName });
    }
    if (costPayoutModifier.conditionFulfillmentService) {
        conditions.push({ label: "Fulfillment", value: costPayoutModifier.conditionFulfillmentService });
    }
    conditions.push({ label: "Priority", value: String(costPayoutModifier.priority) });

    return (
        <Pane>
            <h3 className={"multi-tab-heading"} style={{ marginBottom: 0 }}>{costPayoutModifier.name}</h3>
            <Pane display={"flex"} flexWrap={"wrap"} gap={8} paddingLeft={36} marginTop={16} marginBottom={30}>
                {conditions.map(({ label, value }) => (
                    <Badge
                        key={label}
                        color={"neutral"}
                        textTransform={"none"}
                        fontWeight={"normal"}
                        paddingX={10}
                        borderRadius={10}
                    >
                        <strong>{label}</strong>&ensp;{value}
                    </Badge>
                ))}
            </Pane>
        </Pane>
    );
};

const TabContentCostMetadata: FC<{ costMetadata: CostMetadata[]; cpmName: string }> = ({
    costMetadata,
    cpmName,
}) => {
    const costMetadataTableCellFactory: TableCellFactory<CostMetadataTableRow, CostMetadata> = useMemo(() => {
        return new CostMetadataTableCellFactory();
    }, []);

    const queryTableData = useCallback((_input: QueryInput<CostMetadataTableRow>) => {
        return Promise.resolve(costMetadata);
    }, [costMetadata]);

    const onUpdateRow = useCallback((
        costMetadataList: CostMetadata[],
    ): CostMetadata[] => {
        return costMetadataList;
    }, []);

    const emptyViewProps = useMemo(() => {
        return {
            primaryText: "No cost metadata linked to this modifier",
            secondaryText: "",
            icon: <Pane marginTop={80}><ProjectsIcon size={60} /></Pane>,
        };
    }, []);

    const errorViewProps = useMemo(() => {
        return {
            primaryText: "Failed to load cost metadata",
            secondaryText: "Refresh the page to try again",
        };
    }, []);

    return (
        <AbstractTableViewProvider
            queryTableData={queryTableData}
            onUpdateRow={onUpdateRow}
            updateRowLoading={false}
            updateRowCalled={false}
            updateRowError={false}
            createRowCalled={false}
            createRowError={undefined}
            createRowLoading={false}
            tableCellFactory={costMetadataTableCellFactory}
            savedTableConfigProps={{
                tableName: "cpmDetailCostMetadata",
                identifiers: ["v2", cpmName],
                defaultConfigValue: DefaultCostMetadataSideSheetTableConfig,
            }}
        >
            <TableView
                errorViewProps={errorViewProps}
                emptyViewProps={emptyViewProps}
            />
        </AbstractTableViewProvider>
    );
};

const TabContentPLPR: FC<{ payoutRuleMappings: ProductListingToPayoutRule[]; cpmName: string }> = ({
    payoutRuleMappings,
    cpmName,
}) => {
    const plprTableCellFactory: PLPRTableCellFactory = useMemo(() => {
        return new PLPRTableCellFactory();
    }, []);

    const queryTableData = useCallback((_input: QueryInput<PLPRTableRow>) => {
        return Promise.resolve(payoutRuleMappings);
    }, [payoutRuleMappings]);

    const onUpdateRow = useCallback((
        plprList: ProductListingToPayoutRule[],
    ): ProductListingToPayoutRule[] => {
        return plprList;
    }, []);

    const emptyViewProps = useMemo(() => {
        return {
            primaryText: "No payout rule mappings linked to this modifier",
            secondaryText: "",
            icon: <Pane marginTop={80}><ProjectsIcon size={60} /></Pane>,
        };
    }, []);

    const errorViewProps = useMemo(() => {
        return {
            primaryText: "Failed to load payout rule mappings",
            secondaryText: "Refresh the page to try again",
        };
    }, []);

    return (
        <>
            <AbstractTableViewProvider
                queryTableData={queryTableData}
                onUpdateRow={onUpdateRow}
                updateRowLoading={false}
                updateRowCalled={false}
                updateRowError={false}
                createRowCalled={false}
                createRowError={undefined}
                createRowLoading={false}
                tableCellFactory={plprTableCellFactory}
                savedTableConfigProps={{
                    tableName: "cpmDetailPLPR",
                    identifiers: ["v2", cpmName],
                    defaultConfigValue: DefaultPLPRSideSheetTableConfig,
                }}
            >
                <TableView
                    errorViewProps={errorViewProps}
                    emptyViewProps={emptyViewProps}
                />
            </AbstractTableViewProvider>
        </>
    );
};
